import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { useMediaQuery } from '~/core/scripts/mediaquery-helper';

const Wrapper = styled.button`
  appearance: none;
  cursor: pointer;
  border: none;
  margin: 0;
  padding: 0;
  position: absolute;
  z-index: 999;
  &&,
  &:hover,
  &:active,
  &:focus {
    cursor: default;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }

  ${(p) =>
    p.isDesktop
      ? `
    right: 12px;
    bottom: 110px;
    padding: 12px;
    border-radius: 4px;
    width: 120px;
    text-align: left;
    font-size: 12px;
  `
      : `
    right: 0;
    bottom: 18px;
    padding: 8px;
    border-radius: 4px 0 0 4px;
    text-align: right;
    font-size: 11px;
  `}
`;

const Title = styled.div`
  font-weight: bold;
  line-height: 1.2;
  margin: 0;
`;

const Body = styled.div`
  line-height: 1;
`;

const CirclesWrapper = styled.figure`
  margin: 4px 0 0;
  display: flex;
  ${(p) =>
    p.isDesktop
      ? `
    width: 96px;
    flex-wrap: wrap;
  `
      : `
    justify-content: flex-end;
    align-items: center;
    min-width: 40px;
  `}
`;

const Circle = styled.div`
  height: 14px;
  width: 14px;
  flex: 0 0 14px;
  border-radius: 50%;
  background-color: ${(p) => p.color};
  ${(p) =>
    p.isDesktop
      ? `
    margin-top: 3px;
  `
      : `
    flex: 0 0 14px;
    margin-right: -3px;
    transition: margin 100ms ease-in-out;
    ${
      p.isCollapsed &&
      `
      &:nth-of-type(even) {
        margin-right: -14px;
      }
    `
    }
    &:first-of-type {
      margin-left: 3px;
    }
    &:last-of-type {
      margin-right: 3px;
    }
  `}
`;

const MobileLabel = styled.span`
  ${(p) => p.isDesktop && 'display: none;'}
  white-space: pre;
  font-weight: 400;

  &:first-of-type {
    padding-right: 2px;
  }
  &:last-of-type {
    text-align: left;
  }
`;

const DesktopLabel = styled.span`
  white-space: pre;
  font-weight: 400;
  flex: 0 0 76px;
  margin: 3px 0 0 6px;
  text-align: left;
`;

function CustomLegend({ scale }) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const range = scale.range();
  const domain = scale.domain();
  const formatDomainLabel = (i) => {
    if (i === 0) {
      return '< ' + domain[i];
    } else if (i > domain.length - 1) {
      return '> ' + domain[domain.length - 1];
    } else {
      return domain[i - 1] + ' - ' + domain[i];
    }
  };
  return (
    <Wrapper isDesktop={isDesktop} onClick={() => setIsCollapsed(!isCollapsed)}>
      <Title isDesktop={isDesktop}>
        {isDesktop
          ? 'Neuinfektionen pro 100.000 EW'
          : isCollapsed
          ? '< Info'
          : 'Neuinf. pro 100.000 EW'}
      </Title>
      <Body>
        <CirclesWrapper isDesktop={isDesktop}>
          {!isDesktop && !isCollapsed && <MobileLabel>&lt; {domain[0]}</MobileLabel>}
          {range.map((color, i) => (
            <Fragment key={`legend-item-${i}`}>
              <Circle isDesktop={isDesktop} isCollapsed={isCollapsed} color={color} />
              {isDesktop && <DesktopLabel>{formatDomainLabel(i)}</DesktopLabel>}
            </Fragment>
          ))}
          {!isDesktop && !isCollapsed && (
            <MobileLabel>&gt; {domain[domain.length - 1]}</MobileLabel>
          )}
        </CirclesWrapper>
      </Body>
    </Wrapper>
  );
}

CustomLegend.propTypes = {
  scale: PropTypes.func.isRequired,
};

export default CustomLegend;
